import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Stage, Layer, Rect, Text, Image as KonvaImage, Ellipse, Line, Group } from 'react-konva';
import { CanvasElement, STAGE_WIDTH, STAGE_HEIGHT, getProxiedImageUrl } from './canvasElements';

export interface PlaceholderData {
  müşteriAdı?: string;
  müşteriVergiNo?: string;
  müşteriVergiDairesi?: string;
  müşteriAdres?: string;
  müşteriTelefon?: string;
  müşteriEmail?: string;
  teklifNo?: string;
  teklifTarihi?: string;
  gecerlilikTarihi?: string;
  firmaAdı?: string;
  firmaTelefon?: string;
  firmaEmail?: string;
  firmaAdres?: string;
  genelToplam?: string;
  paraBirimi?: string;
  kdvToplam?: string;
  araToplam?: string;
}

function resolvePlaceholders(text: string, data?: PlaceholderData): string {
  if (!data || !text) return text;
  return text.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
    const val = (data as any)[key.trim()];
    return val !== undefined && val !== null ? String(val) : match;
  });
}

interface CanvasPageRendererProps {
  elements: CanvasElement[];
  bgColor?: string;
  bgGradientFrom?: string;
  bgGradientTo?: string;
  onImageReady?: (dataUrl: string) => void;
  placeholderData?: PlaceholderData;
}

const useLoadedImage = (src?: string) => {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!src) { setImg(null); return; }
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => setImg(image);
    image.onerror = () => setImg(null);
    image.src = getProxiedImageUrl(src);
  }, [src]);
  return img;
};

const RenderImage: React.FC<{ el: CanvasElement }> = ({ el }) => {
  const img = useLoadedImage(el.src);
  if (!img) return null;
  return (
    <KonvaImage
      image={img}
      x={el.x} y={el.y}
      width={el.width || 200} height={el.height || 150}
      rotation={el.rotation || 0} opacity={el.opacity ?? 1}
    />
  );
};

const RenderElement: React.FC<{ el: CanvasElement; pData?: PlaceholderData }> = ({ el, pData }) => {
  switch (el.type) {
    case 'text':
      return (
        <Text
          x={el.x} y={el.y} width={el.width}
          text={resolvePlaceholders(el.text || '', pData)} fontSize={el.fontSize || 16}
          fontFamily={el.fontFamily || 'Arial'} fontStyle={el.fontStyle || 'normal'}
          fill={el.fill || '#000'} align={el.align || 'left'}
          lineHeight={el.lineHeight || 1.4}
          rotation={el.rotation || 0} opacity={el.opacity ?? 1}
        />
      );
    case 'image':
      return <RenderImage el={el} />;
    case 'rect':
      return (
        <Rect
          x={el.x} y={el.y} width={el.width || 200} height={el.height || 100}
          fill={el.fillColor || '#f0f4ff'} stroke={el.stroke || ''}
          strokeWidth={el.strokeWidth || 0} cornerRadius={el.cornerRadius || 0}
          rotation={el.rotation || 0} opacity={el.opacity ?? 1}
        />
      );
    case 'circle':
      return (
        <Ellipse
          x={el.x} y={el.y} radiusX={el.radiusX || 60} radiusY={el.radiusY || 60}
          fill={el.fillColor || '#4a90d9'} stroke={el.stroke || ''}
          strokeWidth={el.strokeWidth || 0}
          rotation={el.rotation || 0} opacity={el.opacity ?? 1}
        />
      );
    case 'line':
      return (
        <Line
          x={el.x} y={el.y} points={el.points || [0, 0, 200, 0]}
          stroke={el.lineStroke || '#ccc'} strokeWidth={el.lineStrokeWidth || 2}
          rotation={el.rotation || 0} opacity={el.opacity ?? 1}
        />
      );
    case 'statBox':
      return (
        <Group x={el.x} y={el.y} rotation={el.rotation || 0} opacity={el.opacity ?? 1}>
          <Rect
            width={el.width || 160} height={el.height || 100}
            fill={el.statBgColor || '#fff'} stroke={el.statBorderColor || '#e0e0e0'}
            strokeWidth={1} cornerRadius={el.cornerRadius || 8}
          />
          <Text
            x={10} y={15} width={(el.width || 160) - 20}
            text={resolvePlaceholders(el.statValue || '', pData)} fontSize={el.statValueSize || 36}
            fontFamily="Arial" fontStyle="bold" fill={el.statValueColor || '#1a3a7a'}
            align="center"
          />
          <Text
            x={10} y={(el.height || 100) - 30} width={(el.width || 160) - 20}
            text={resolvePlaceholders(el.statLabel || '', pData)} fontSize={el.statLabelSize || 13}
            fontFamily="Arial" fill={el.statLabelColor || '#666'} align="center"
          />
        </Group>
      );
    default:
      return null;
  }
};

const CanvasPageRenderer: React.FC<CanvasPageRendererProps> = ({
  elements, bgColor, bgGradientFrom, bgGradientTo, onImageReady, placeholderData,
}) => {
  const stageRef = useRef<any>(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const checkImages = useCallback(() => {
    const imageEls = elements.filter(e => e.type === 'image' && e.src);
    if (imageEls.length === 0) { setImagesLoaded(true); return; }
    // Yeni elemanlarda henüz yüklenmemiş görseller olabilir; önce sıfırla
    setImagesLoaded(false);
    let loaded = 0;
    imageEls.forEach(el => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { loaded++; if (loaded >= imageEls.length) setImagesLoaded(true); };
      img.onerror = () => { loaded++; if (loaded >= imageEls.length) setImagesLoaded(true); };
      img.src = getProxiedImageUrl(el.src!);
    });
  }, [elements]);

  useEffect(() => { checkImages(); }, [checkImages]);

  // Elemanlar/arka plan/veri değiştiğinde görüntü yeniden üretilmeli;
  // aksi halde önceki şablonun bayat görüntüsü PDF'e gider
  useEffect(() => {
    if (imagesLoaded && onImageReady && stageRef.current) {
      const timer = setTimeout(() => {
        if (!stageRef.current) return;
        const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
        onImageReady(dataUrl);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [imagesLoaded, onImageReady, elements, bgColor, bgGradientFrom, bgGradientTo, placeholderData]);

  const hasBgGradient = bgGradientFrom && bgGradientTo;

  return (
    <div style={{ position: 'absolute', left: -9999, top: -9999, pointerEvents: 'none' }}>
      <Stage ref={stageRef} width={STAGE_WIDTH} height={STAGE_HEIGHT}>
        <Layer>
          <Rect
            x={0} y={0} width={STAGE_WIDTH} height={STAGE_HEIGHT}
            fill={hasBgGradient ? undefined : (bgColor || '#ffffff')}
            fillLinearGradientStartPoint={hasBgGradient ? { x: 0, y: 0 } : undefined}
            fillLinearGradientEndPoint={hasBgGradient ? { x: STAGE_WIDTH, y: STAGE_HEIGHT } : undefined}
            fillLinearGradientColorStops={hasBgGradient ? [0, bgGradientFrom!, 1, bgGradientTo!] : undefined}
          />
          {elements.map(el => <RenderElement key={el.id} el={el} pData={placeholderData} />)}
        </Layer>
      </Stage>
    </div>
  );
};

export default CanvasPageRenderer;
